import { h, Host } from "@stencil/core";
import katex from "katex";
/**
 * A component for rendering LaTeX math expressions using KaTeX.
 *
 * To use this component, you must include the necessary custom fonts in your
 * project. These custom fonts are located in the
 * `node_modules/@genexus/chameleon-controls-library/dist/assets/fonts` folder.
 *
 * To declare the font-faces of these custom fonts in your project, you must
 * use the `math-viewer-font-faces` mixin located in the
 * `node_modules/@genexus/chameleon-controls-library/dist/assets/scss/math-viewer-font-face.scss` folder
 */
export class ChMathViewer {
    constructor() {
        this.renderedBlocks = [];
        this.displayMode = "block";
        this.value = undefined;
    }
    updateRenderedBlocks() {
        if (!this.value) {
            this.renderedBlocks = [];
            return;
        }
        const blocks = this.value
            .split(/\n\s*\n/) // split on empty lines (paragraphs)
            .map(b => b.trim())
            .filter(b => b);
        const htmlBlocks = blocks.map(block => {
            const isMathBlock = block.startsWith("\\[") ||
                block.startsWith("\\(") ||
                block.startsWith("$$") ||
                block.startsWith("\\begin") ||
                block.includes("&=") ||
                block.includes("^");
            // Remove unnecessary delimiters for KaTeX processing
            const cleanBlock = block
                .replace(/^\\\[|\\\]$/g, "")
                .replace(/^\\\(|\\\)$/g, "")
                .replace(/^\$\$|\$\$$/g, "")
                .replace(/^\$|\$$/g, "");
            try {
                return {
                    html: katex.renderToString(cleanBlock, {
                        throwOnError: true,
                        displayMode: isMathBlock || this.displayMode === "block",
                        output: "htmlAndMathml" // For accessibility reasons
                    }),
                    error: null
                };
            }
            catch (err) {
                return { html: cleanBlock, error: err.message };
            }
        });
        this.renderedBlocks = htmlBlocks;
    }
    componentWillLoad() {
        this.updateRenderedBlocks();
    }
    render() {
        return (h(Host, { key: '15b6166d1cf6882d5d21af1357e52755acf850f5' }, this.renderedBlocks.map(({ html, error }) => error === null ? (h("div", { innerHTML: html })) : (h("span", { "aria-description": error, title: error, class: "katex", part: "error" }, html)))));
    }
    static get is() { return "ch-math-viewer"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["math-viewer.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["math-viewer.css"]
        };
    }
    static get properties() {
        return {
            "displayMode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"block\" | \"inline\"",
                    "resolved": "\"block\" | \"inline\"",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies whether to render the math in block or inline mode."
                },
                "attribute": "display-mode",
                "reflect": true,
                "defaultValue": "\"block\""
            },
            "value": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the LaTeX math string to render."
                },
                "attribute": "value",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "renderedBlocks": {}
        };
    }
    static get watchers() {
        return [{
                "propName": "value",
                "methodName": "updateRenderedBlocks"
            }];
    }
}
